/**
 * The run-done column: where the coach waits after a pass or a run, and the whole
 * list at once when somebody asks for it.
 *
 * It is named for the bar that used to head it — one strip across the top of the
 * field saying a run had finished, with the coach's count, STEP THROUGH, DIGEST,
 * ORDER, SEE THE ACCOUNT and a ✕. The study counted that bar among the four places
 * a run's controls were spread over, and it folded away (see `ui/RunStrip.tsx`):
 * the run's ending, the account and Continue went to the run strip above the
 * input, where the press that started the run and the one that stopped it
 * already are. What was the coach's stayed the coach's, here, and nothing of it
 * was dropped:
 *
 *   the count          → the quiet line, "3 things to look at next ›" (`CoachLine`),
 *                        which now counts while a run's close holds the coach up too
 *   DIGEST / PINS      → the line's own press (L at the keyboard), and a digest
 *                        row back to the card and its pins
 *   STEP THROUGH       → a digest row, which opens the card on that cue (G too)
 *   ORDER              → the digest's foot (O while the card is open)
 *   PUT AWAY · MUTED   → the digest's foot, and the line itself once everything
 *   and its restore      on the board has been answered, so the way back is on
 *                        screen at exactly the moment it is earned
 *   the bar's ✕        → a ✕ beside the line while the card or pins are up; the
 *                        run's own ending has its ✕ on the run strip
 *
 * Geometry is MEASURED, never a literal. The left edge is whatever the session
 * column's box actually is — a live run's thinking changes that width, and the
 * explorations sidebar moves the whole column sideways — and the right edge
 * rides `viewInset.right` — the window's own edge since the reading pane moved
 * to the left — the way the tool dock does. Where the column is not beside the
 * field's top edge at all, the panes' columns are read from `viewInset.left`
 * instead, for the same reason.
 *
 * The digest hangs beneath the line and shares those edges by CONSTRUCTION rather
 * than by repeating them: the measurement is on a positioned column and the
 * surfaces are its rows, so there is one place the left edge is decided and the
 * rows below cannot drift out from under the first.
 *
 * At phone width the coach's card is a row here too, for that same reason — the
 * column is one measurement of the field's edges and a card measuring its own
 * copy is how the two would drift. Above the breakpoint the card is a popup
 * beside its node instead and this file knows nothing about it (D25).
 */

import { useId, useLayoutEffect, useState, type CSSProperties } from 'react'
import { CUE_LABEL, type Cue } from '../core/cues'
import { coachRetirement, type CoachState } from '../core/coach'
import { useStore } from '../core/store'
import { STATUS_WORD, worthALook } from '../core/vocabulary'
import { Icon } from './components/Icon'
import { useMedia } from './components/useMedia'
import {
  COACH_LENS_LABEL,
  CoachColumnCard,
  CuePressText,
  cycleCoachLens,
  takeCueAction,
} from './Coach'
import {
  cueAuditor,
  cuePressName,
  cuePressTail,
  useCardShows,
  useCoachGate,
  useCoachHeading,
  useCoachLineCount,
  useDigestShows,
  useLiveCoach,
  useVisibleCues,
} from './coachView'

/** The gap the column keeps from the session column and from the field's edges —
 *  the chrome's own 16, so it lines up with the column above it. */
const PAD = 16

/**
 * How many cues the digest lists.
 *
 * Four, not eight. The digest is the glance — "what is on the board" — and the
 * card is the reading. A list as long as the pin set would be the pin set again
 * in words, sitting on top of the graph it is describing; four fits under the
 * line without covering the field, and the fifth cue is one press of J away once
 * a row has opened the card.
 */
const DIGEST_ROWS = 4

/** What this exploration has put away, as the line and the digest's foot say it —
 *  the two halves `coachRetirement` counts, and the press that brings both back. */
type Retired = { retired: number; muted: number }
const retiredWords = ({ retired, muted }: Retired): string =>
  [
    retired > 0 ? `${retired} put away` : null,
    muted > 0 ? `${muted} kind${muted === 1 ? '' : 's'} muted` : null,
  ]
    .filter(Boolean)
    .join(' · ')
const RESTORE_WHY = 'Bring back everything you have skipped or silenced in this exploration.'

export function RunDoneBar() {
  // Through `liveCoach`: this column reports what the coach reads on THIS graph,
  // and a coach belonging to another session would print another exploration's
  // counts over it.
  const coach = useLiveCoach()
  const grown = useStore((s) => s.session.order.length >= 2)
  const viewInset = useStore((s) => s.viewInset)
  const narrow = useMedia('(max-width: 767px)')
  const gate = useCoachGate()
  const cues = useVisibleCues()
  const digest = useDigestShows()
  const card = useCardShows()
  const lineCount = useCoachLineCount()
  const heading = useCoachHeading()
  const digestId = useId()

  // The session column, measured. It is always on screen — a named, sometimes
  // empty, rectangle — so there is something to measure whenever this is up.
  //
  // Measured rather than written down even though the column's width is
  // currently a fixed 326px: that width is `min()`-ed against the viewport and
  // capped against the tool dock's band (App.tsx), so it is only constant on a
  // wide screen, and a literal here would be a second copy of a number owned
  // over there. The observer covers the rest — the column resizing under a
  // narrow viewport, and a live run's thinking arriving in it.
  // Right edge for the wide layout, bottom edge for the narrow one: below the
  // breakpoint there is no room BESIDE the column, so narrow goes UNDER it
  // instead, at whatever height it actually is.
  const [column, setColumn] = useState({ right: 0, bottom: 0, viewport: 0 })
  // Layout, not effect: measured before the browser paints, so the column never
  // shows for one frame sitting on top of the one it is meant to sit beside.
  useLayoutEffect(() => {
    const el = document.querySelector('.slot-session')
    if (!el) return
    const measure = () => {
      const box = el.getBoundingClientRect()
      setColumn({
        right: Math.round(box.right),
        bottom: Math.round(box.bottom),
        viewport: window.innerWidth,
      })
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    window.addEventListener('resize', measure)
    // And again once the column's own transition ends. It declares none, and
    // moves in one step, but the reduced-motion rule in global.css gives EVERY
    // element a 0.01ms duration over the default `transition-property: all`,
    // so under reduced motion its `left` is a transition after all. A
    // transition just begun reports its starting value until the next frame,
    // so the `measure()` above, run in the commit that moved the column, read
    // the box it was leaving, and a move is not a resize, so the observer
    // never corrected it: the line stood beside where the column HAD been,
    // under the thread pane, which took its clicks. The column's own events
    // only: its children's transitions bubble here too, and a change in their
    // size is the observer's.
    const settled = (e: Event) => {
      if (e.target === el) measure()
    }
    el.addEventListener('transitionend', settled)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', measure)
      el.removeEventListener('transitionend', settled)
    }
    // `viewInset.left` is in the deps and it is not decoration: the sidebar
    // collapsing MOVES the session column without resizing it — its width is
    // `min(326px, …)`, which is 326 on both sides of the change — so the
    // observer never fires and this would keep the old column's right edge,
    // stranded 228px into the field the pane just gave back.
  }, [narrow, viewInset.left])

  // The beside-the-column form needs room for the digest's rows. At a laptop
  // width with the thread open, the span between the session column — pushed
  // right by the pane — and the window's edge can fall short, and a row's title
  // and its action would be squeezed to nothing. So the below-column form is not
  // the phone's alone: it applies whenever the MEASURED span cannot hold the
  // digest's own width, whatever the breakpoint says.
  const ROW_MIN = 620
  const cramped =
    !narrow && column.viewport > 0 && column.viewport - column.right - viewInset.right < ROW_MIN

  // Both halves of the way back, counted in `core/coach.ts` so these presses and
  // the test that pins them read one arithmetic. `retired` is the half that used
  // to be missing: skipping a board where every kind has one instance empties it
  // without silencing a single kind.
  const { silenced, retired, restorable } = coach
    ? coachRetirement(coach)
    : { silenced: [] as string[], retired: 0, restorable: false }
  const put: Retired = { retired, muted: silenced.length }
  // The board answered to nothing, with something to bring back: the line stays
  // up as the way back rather than vanishing with the count. Behind the same
  // gate as every coach surface, and the same floor of a graph with something
  // grown on it.
  const restoreOnly = gate && grown && lineCount === 0 && restorable
  const showLine = lineCount > 0 || restoreOnly
  // The card's phone form is the column's last row (ui/Coach.tsx). At this width
  // there is no room beside a node, so the card comes here instead of onto the
  // graph — and it comes INTO this column rather than measuring its own copy of
  // these edges. Asked separately from the digest, and never both at once:
  // `toggleCoachDigest` closes one to open the other.
  const showCard = narrow && card
  if (!showLine && !digest && !showCard) return null

  return (
    <div
      className="rundonecol"
      style={
        narrow
          ? // The sidebar's inset is 0 here by construction — below the
            // breakpoint it is an overlay, not a column — so this arm reads the
            // same 16 it always did, through the one expression rather than a
            // literal that would go wrong if that ever changed.
            ({
              left: viewInset.left + PAD,
              right: PAD,
              top: column.bottom + 8,
            } as CSSProperties)
          : cramped
            ? // Below the column like the phone, but both panes still inset the
              // edges here — the sheet-vs-rail seam is the breakpoint's, not
              // this fallback's. Without the left one the column starts under
              // the sidebar's glass at exactly the widths this arm exists for.
              ({
                left: viewInset.left + PAD,
                right: viewInset.right + PAD,
                top: column.bottom + 8,
              } as CSSProperties)
            : ({ left: column.right + PAD, right: viewInset.right + PAD } as CSSProperties)
      }
    >
      {showLine && (
        <CoachLine
          count={lineCount}
          open={digest}
          controls={digestId}
          raised={!!coach && (coach.bar || coach.open)}
          restore={restoreOnly ? put : null}
        />
      )}
      {digest && coach && (
        <CoachDigest
          cues={cues}
          id={digestId}
          heading={heading}
          lens={coach.lens}
          restore={restorable ? put : null}
        />
      )}
      {showCard && <CoachColumnCard />}
    </div>
  )
}

/**
 * The coach at rest: one line saying how many things to look at next, and the
 * press that lists them.
 *
 * This is where the coach waits instead of opening itself. A seed's opening
 * raised the card over the ring it had just landed — headed "After the run" over
 * one pass that was not a run, standing on the claims it described — and every
 * pass after it left the same offer hanging over the graph. A count is the part
 * of that a reader needs before they ask for more, and this column is where it is
 * on screen at every width without taking a line from the input's surroundings.
 *
 * It opens the DIGEST, never the card: a list is a glance, the card is a reading,
 * and a reading is one more press (a row) away. No camera walk, no pins, nothing
 * spent. Pressed again, it puts the list away and retires nothing — the same soft
 * close Escape takes.
 *
 * Two things joined it when the bar above it folded away. A ✕, while the card or
 * the pins are up: it is the bar's ✕ for the coach's half — `closeCoachBar`, the
 * card and the pins down, nothing retired — and on a phone, with no Escape key,
 * it is the only way to put a run's card away. And the way back: once every cue
 * on the board has been answered, the line says what was put away and its press
 * brings it back, where a count of nothing would have said nothing at all.
 */
function CoachLine({
  count,
  open,
  controls,
  raised,
  restore,
}: {
  count: number
  open: boolean
  controls: string
  raised: boolean
  restore: Retired | null
}) {
  const openCoachDigest = useStore((s) => s.openCoachDigest)
  const dismissCoach = useStore((s) => s.dismissCoach)
  const closeCoachBar = useStore((s) => s.closeCoachBar)
  const unmuteCues = useStore((s) => s.unmuteCues)
  return (
    <div className="coachline-row">
      {restore ? (
        <button className="coachline panel" title={RESTORE_WHY} onClick={unmuteCues}>
          {retiredWords(restore)} · bring them back
        </button>
      ) : (
        <button
          className="coachline panel"
          aria-expanded={open}
          aria-controls={open ? controls : undefined}
          title={
            open
              ? 'Put the list away. Nothing is retired.'
              : 'What the coach would read first on the graph as it stands, as a list. Nothing is spent.'
          }
          onClick={() => (open ? dismissCoach() : openCoachDigest())}
        >
          {worthALook(count)}
          <Icon
            name="chevron"
            size={9}
            style={{
              transform: open ? 'rotate(90deg)' : 'none',
              transition: 'transform 0.2s ease',
            }}
          />
        </button>
      )}
      {raised && (
        <button
          className="coachline-close panel"
          aria-label="Put the coach away"
          title="Puts the card and the pins away. Nothing is retired, and the line stays."
          onClick={closeCoachBar}
        >
          <Icon name="close" size={9} />
        </button>
      )}
    </div>
  )
}

/**
 * The whole board at a glance: the top four, in the order the lens set.
 *
 * Two presses per row, and they are deliberately different presses. The row body
 * leaves the digest and lands the card on that cue — reading, which costs
 * nothing. The button on the right is the cue's own action with its own label,
 * routed through `takeCueAction` like every other press of a cue, so a cue
 * cannot come to mean one thing here and another on the card.
 *
 * The row is NOT the action. A list where the whole row spends is a list you
 * cannot browse.
 *
 * Its foot carries what the bar carried about the list as a whole: the order it
 * is read in, and what has been put away with the press that brings it back.
 * Both are about THIS list, so they sit under it rather than on a strip of their
 * own across the field.
 */
function CoachDigest({
  cues,
  id,
  heading,
  lens,
  restore,
}: {
  cues: Cue[]
  id: string
  heading: string
  lens: CoachState['lens']
  restore: Retired | null
}) {
  const toggleCoachDigest = useStore((s) => s.toggleCoachDigest)
  const setCoachIndex = useStore((s) => s.setCoachIndex)
  const unmuteCues = useStore((s) => s.unmuteCues)
  // The five fields `cueAuditor` reads, and the routing the press's name prices
  // through, subscribed one by one so this stays reactive without pulling the
  // whole store in.
  const session = useStore((s) => s.session)
  const modelId = useStore((s) => s.modelId)
  const roleModels = useStore((s) => s.roleModels)
  const keys = useStore((s) => s.keys)
  const customBase = useStore((s) => s.customBase)
  const customModel = useStore((s) => s.customModel)

  return (
    <div className="coachdigest panel">
      {/* The name goes on the list, not on the wrapper: `aria-label` on a plain
          div names nothing, because a div carries no role to be named. */}
      <ul
        id={id}
        aria-label={
          heading === STATUS_WORD.afterRun ? 'What the run left, in order' : `${heading}, in order`
        }
      >
        {cues.slice(0, DIGEST_ROWS).map((cue, i) => {
          // Resolved exactly as the card resolves it, so the figure here and the
          // figure there are one arithmetic on one auditor.
          const auditor = cueAuditor({ session, modelId, keys, customBase, customModel }, cue)
          return (
            <li key={cue.key} className="coachdigest__row">
              <button
                className="coachdigest__read"
                title="Read this one beside the node it is about."
                onClick={() => {
                  // Digest off first, which is what reopens the card, then the
                  // position — `setCoachIndex` is the one writer of the index
                  // and the card reads the same clamp the pins do. A third press
                  // stood here, `setRail(true)`, for as long as the card lived
                  // inside the inspector: without it this row silently did
                  // nothing on a phone, where the sheet is shut and nothing else
                  // opens it. The card no longer lives there, so the row is back
                  // to the two presses it is actually about (D25).
                  toggleCoachDigest()
                  setCoachIndex(i)
                }}
              >
                <span className="coachdigest__n">{i + 1}</span>
                <span
                  className="coachdigest__kind"
                  // The ink tier, not the mark tier: this is 10px type on a
                  // panel, and D16's rule is that a letterform reads through
                  // `--ink-*` while a surface takes `--sem-*`. The card sets
                  // both for the same cue; here only the text exists.
                  style={{ '--cue-ink': `var(--ink-${cue.channel})` } as CSSProperties}
                >
                  {CUE_LABEL[cue.kind]}
                </span>
                <span className="coachdigest__title">{cue.title}</span>
              </button>
              {cue.action && (
                // Not disabled on a refusal, unlike the card: the card has room
                // for the paragraph that explains one and a row here does not,
                // so `takeCueAction` refuses out loud with the same sentence
                // instead of greying out silently. Same predicate either way —
                // `cueRefusal` — so the two surfaces cannot disagree about
                // whether the press can happen, only about how they say so.
                <button
                  className="coachdigest__act"
                  title="The same press the card offers, and the same one Enter takes."
                  // The card's own name for it, claim and estimate included.
                  aria-label={cuePressName(
                    cue,
                    cuePressTail(
                      { modelId, roleModels, keys, customBase, customModel },
                      cue,
                      auditor?.entry ?? null,
                    ),
                  )}
                  onClick={() => takeCueAction(cue)}
                >
                  <CuePressText cue={cue} auditor={auditor?.entry ?? null} />
                </button>
              )}
            </li>
          )
        })}
      </ul>
      <div className="coachdigest__foot">
        <button
          className="coachdigest__foot-act"
          title="The order they are read in. Nothing here scores a claim — it is which one this app would read first."
          onClick={cycleCoachLens}
        >
          Order · {COACH_LENS_LABEL[lens]}
        </button>
        {restore && (
          <button className="coachdigest__foot-act" title={RESTORE_WHY} onClick={unmuteCues}>
            {retiredWords(restore)} · bring back
          </button>
        )}
      </div>
    </div>
  )
}
